import { settingsApiClient } from "@api/settings-api-client";
import { CompanyId, UserId } from "@custom-types/base-types";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { SettingsApiTypes } from "@stellar/settings-api-client";
import {
  CompanyBatchSettingsResponse,
  CompanySettingsIdentifier,
  CompanySettingsResponse,
  UserBatchSettingsResponse,
  UserSettingsIdentifier,
  UserSettingsResponse,
} from "@store/settings/settings-slice-types";

interface FetchBatchUserSettingsProps {
  /** The identifiers of the user settings to fetch */
  identifiers: UserSettingsIdentifier[];
  /** The user ID for whom the settings are being fetched */
  userId: UserId;
}

/**
 * Fetches user settings in batch.
 * @throws {Error} if the Settings API client request fails
 */
export const fetchUserSettings = createAsyncThunk<
  UserBatchSettingsResponse,
  FetchBatchUserSettingsProps
>("settings/fetchUserSettings", async ({ identifiers, userId: _userId }) => {
  return await settingsApiClient.user.fetchUserBatchSettings(identifiers);
});

interface UpdateUserSettingProps<Identifier extends UserSettingsIdentifier> {
  /** The identifier of the setting to update */
  identifier: Identifier;
  /** The user ID for whom the setting is being updated */
  userId: UserId;
  /** The payload to update the setting with */
  payload: SettingsApiTypes.UpdateSettingPayload<"USER", Identifier>;
}

/**
 * Updates a single user setting and returns the updated setting.
 */
export const updateUserSetting = createAsyncThunk<
  { userId: UserId; setting: UserSettingsResponse },
  UpdateUserSettingProps<UserSettingsIdentifier>
>("settings/updateUserSetting", async ({ identifier, userId, payload }) => {
  const updatedSetting = await settingsApiClient.user.updateUserSettings(
    identifier,
    payload
  );
  return {
    userId,
    setting: updatedSetting,
  };
});

interface FetchBatchCompanySettingsProps {
  /** The identifiers of the company settings to fetch */
  identifiers: CompanySettingsIdentifier[];
  /** The company ID for which settings are being fetched */
  companyId: CompanyId;
}

/** Fetches company settings in batch */
export const fetchCompanySettings = createAsyncThunk<
  CompanyBatchSettingsResponse,
  FetchBatchCompanySettingsProps
>("settings/fetchCompanySettings", async ({ identifiers, companyId }) => {
  return await settingsApiClient.company.fetchCompanyBatchSettings(
    companyId,
    identifiers
  );
});

interface UpdateCompanySettingProps<
  Identifier extends CompanySettingsIdentifier,
> {
  /** The identifier of the setting to update */
  identifier: Identifier;

  /** The company ID for which the setting is being updated */
  companyId: CompanyId;

  /** The payload to update the setting with */
  payload: SettingsApiTypes.UpdateSettingPayload<"COMPANY", Identifier>;
}

/**
 * Updates a single company setting and returns the updated setting.
 */
export const updateCompanySetting = createAsyncThunk<
  { companyId: CompanyId; setting: CompanySettingsResponse },
  UpdateCompanySettingProps<CompanySettingsIdentifier>
>(
  "settings/updateCompanySetting",
  async ({ identifier, companyId, payload }) => {
    const updatedSetting =
      await settingsApiClient.company.updateCompanySettings(
        companyId,
        identifier,
        payload
      );

    return {
      companyId,
      setting: updatedSetting,
    };
  }
);
